import { BASE_URL } from "config";
import React, { useState, useEffect, useRef } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import {
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Button,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormGroup,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useMaterialUIController, setDirection } from "context";
import BundleOfferSection from "./BundleOfferSection";
import XYOfferSection from "./XYOfferSection";
import CouponSection from "./CouponSection";
import PurchaseRewardSection from "./PurchaseRewardSection";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import Swal from "sweetalert2";
import ConstraintsSection from "./ConstraintsSection";
import ExclusionsSection from "./ExclusionsSection";

const targetAudienceOptions = [
  { value: "all", label: "الكل" },
  { value: "new_users", label: "المستخدمين الجدد فقط" },
  { value: "logged_in_only", label: "المسجلين فقط" },
];

function getToday() {
  const today = new Date();
  return today.toISOString().split("T")[0];
}

export default function OfferDataSection() {
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState("");
  const [offerType, setOfferType] = useState(1);
  const [targetAudience, setTargetAudience] = useState("all");
  const [isSuperOffer, setIsSuperOffer] = useState(false);
  const [isActive, setIsActive] = useState("1");
  const [controller, dispatch] = useMaterialUIController();
  const [errors, setErrors] = useState({});
  const bundleOfferRef = useRef();
  const xyOfferRef = useRef();
  const couponRef = useRef();
  const purchaseRewardRef = useRef();
  const [offerTypes, setOfferTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [constraints, setConstraints] = useState([]);
  const [exclusions, setExclusions] = useState([]);

  useEffect(() => {
    setDirection(dispatch, "rtl");
    return () => setDirection(dispatch, "ltr");
  }, []);

  // جلب أنواع العروض
  useEffect(() => {
    const fetchOfferTypes = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("user-token");
        const response = await axios.get(BASE_URL + "/manager/promotions/create", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("response", response.data);
        if (response.data.status && response.data.data.promotion_types) {
          setOfferTypes(response.data.data.promotion_types);
        }
      } catch (error) {
        console.error("Error fetching offer types:", error);
        Swal.fire({
          title: "خطأ",
          text: "حدث خطأ أثناء جلب أنواع العروض",
          icon: "error",
          confirmButtonText: "حسناً",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchOfferTypes();
  }, []);

  // تحديث نوع العرض عند تغييره
  const handleOfferTypeChange = (event) => {
    const newType = Number(event.target.value);
    setOfferType(newType);
    // إعادة تعيين الأخطاء عند تغيير نوع العرض
    setErrors({});
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // التحقق من الحقول الأساسية
    if (!title || title.trim() === "") {
      newErrors.title = "يرجى إدخال اسم العرض";
      isValid = false;
    }
    if (!startDate) {
      newErrors.startDate = "يرجى إدخال تاريخ البداية";
      isValid = false;
    }
    if (!endDate) {
      newErrors.endDate = "يرجى إدخال تاريخ النهاية";
      isValid = false;
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = "تاريخ النهاية يجب أن يكون بعد تاريخ البداية";
      isValid = false;
    }

    // التحقق من حقول عرض الحزمة فقط إذا كان نوع العرض هو عرض حزمة
    if (offerType === 1) {
      const bundleData = bundleOfferRef.current ? bundleOfferRef.current.getBundleData() : {};
      if (!bundleData.bundle?.name) {
        newErrors.bundleName = "اسم الحزمة إجباري";
        isValid = false;
      }
      if (!bundleData.bundle?.bundle_products || bundleData.bundle.bundle_products.length === 0) {
        newErrors.bundleProducts = "يجب اختيار منتج واحد على الأقل للحزمة";
        isValid = false;
      }

      const giftData = bundleOfferRef.current ? bundleOfferRef.current.getGiftData() : {};
      if (!giftData.rewards || giftData.rewards.promotion_gift_types.length === 0) {
        newErrors.rewards = "يجب اختيار نوع واحد من المكافآت على الأقل";
        isValid = false;
      }
    }

    // التحقق من حقول عرض X Y فقط إذا كان نوع العرض هو عرض X Y
    if (offerType === 2) {
      const xyData = xyOfferRef.current ? xyOfferRef.current.getXYData() : {};
      const giftData = xyOfferRef.current
        ? xyOfferRef.current.getGiftData()
        : { rewards: { promotion_gift_types: [], free_products: [] } };

      if (!xyData.xy_products || xyData.xy_products.length === 0) {
        newErrors.xyProducts = "يجب اختيار منتج واحد على الأقل للعرض";
        isValid = false;
      }

      if (
        !giftData.rewards ||
        (!giftData.rewards.promotion_gift_types.length && !giftData.rewards.free_products.length)
      ) {
        newErrors.rewards = "يجب اختيار نوع واحد من المكافآت على الأقل";
        isValid = false;
      }
    }

    if (offerType === 3) {
      const couponData = couponRef.current.getCouponData();
      if (!couponData.coupon.code) {
        newErrors.coupon = "يرجى إدخال كود الكوبون";
        isValid = false;
      }
      const giftData = couponRef.current.getGiftData();
      if (
        !giftData.rewards?.promotion_gift_types?.length &&
        !giftData.rewards?.free_products?.length
      ) {
        newErrors.rewards = "يرجى اختيار نوع مكافأة واحد على الأقل";
        isValid = false;
      }
    }

    if (offerType === 4) {
      const purchaseRewardData = purchaseRewardRef.current.getPurchaseRewardData();
      if (!purchaseRewardData.purchase_rewards[0]?.amount) {
        newErrors.amount = "يرجى إدخال المبلغ";
        isValid = false;
      }
      const giftData = purchaseRewardRef.current.getGiftData();
      if (
        !giftData.rewards?.promotion_gift_types?.length &&
        !giftData.rewards?.free_products?.length
      ) {
        newErrors.rewards = "يرجى اختيار نوع مكافأة واحد على الأقل";
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };
  // create new offer
  const handleSave = async () => {
    if (!validateForm()) return;

    let data = {
      name: title,
      description: "عرض جديد",
      promotion_type_id: offerType,
      start_date: startDate,
      end_date: endDate,
      is_super_offer: isSuperOffer ? 1 : 0,
      target_audience: targetAudience,
      is_active: isActive,
      promotion_constraints: constraints,
      promotion_exclusions: exclusions.map((ex) => ({ excluded_promotion_id: ex.id.toString() })),
    };

    if (offerType === 1) {
      const bundleData = bundleOfferRef.current ? bundleOfferRef.current.getBundleData() : {};
      const giftData = bundleOfferRef.current ? bundleOfferRef.current.getGiftData() : {};
      data = {
        ...data,
        ...bundleData,
        rewards: giftData.rewards,
      };
    } else if (offerType === 2) {
      const xyData = xyOfferRef.current ? xyOfferRef.current.getXYData() : {};
      const giftData = xyOfferRef.current
        ? xyOfferRef.current.getGiftData()
        : { rewards: { promotion_gift_types: [], free_products: [] } };

      // هيكل بيانات عرض X Y
      data = {
        ...data,
        buy_x_get_y_offer: {
          buy_x_get_y_products: xyData.xy_products.map((product) => ({
            product_id: product.product_id,
            min_quantity: product.min_quantity,
            max_quantity: product.max_quantity || null,
          })),
        },
        rewards: giftData.rewards,
      };
    } else if (offerType === 3) {
      const couponData = couponRef.current.getCouponData();
      const giftData = couponRef.current.getGiftData();
      data = {
        ...data,
        ...couponData,
        rewards: giftData.rewards,
      };
    } else if (offerType === 4) {
      const purchaseRewardData = purchaseRewardRef.current.getPurchaseRewardData();
      const giftData = purchaseRewardRef.current.getGiftData();
      data = {
        ...data,
        ...purchaseRewardData,
        rewards: giftData.rewards,
      };
      console.log("Purchase Reward Data:", {
        ...data,
        ...purchaseRewardData,
        rewards: giftData.rewards,
      });
    }

    console.log("Structured Data:", data);

    try {
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const token = localStorage.getItem("user-token");
      const response = await axios.post(BASE_URL + "/manager/promotions/store", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.status) {
        Swal.fire({
          title: "تم الحفظ بنجاح",
          text: "تم حفظ العرض بنجاح",
          icon: "success",
          confirmButtonText: "حسناً",
        }).then(() => {
          setTitle("");
          setStartDate(getToday());
          setEndDate("");
          setOfferType(1);
          setTargetAudience("all");
          setIsSuperOffer(false);
          setIsActive("1");
          setErrors({});
          if (bundleOfferRef.current) {
            bundleOfferRef.current.reset();
          }
          if (xyOfferRef.current) {
            xyOfferRef.current.reset();
          }
          if (couponRef.current) {
            couponRef.current.reset();
          }
          if (purchaseRewardRef.current) {
            purchaseRewardRef.current.reset();
          }
          setConstraints([]);
          setExclusions([]);
        });
      } else {
        Swal.fire({
          title: "خطأ",
          text: response.data.message || "حدث خطأ أثناء حفظ العرض",
          icon: "error",
          confirmButtonText: "حسناً",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "خطأ",
        text: error.response?.data?.message || "حدث خطأ أثناء حفظ العرض",
        icon: "error",
        confirmButtonText: "حسناً",
      });
    }
  };

  // الحصول على أنواع الهدايا المتاحة لنوع العرض المختار
  const getAvailableGiftTypes = () => {
    const selectedOfferType = offerTypes.find((type) => type.id === offerType);
    console.log("gift types", selectedOfferType?.gift_types);
    return selectedOfferType?.gift_types || [];
  };

  return (
    <MDBox>
      <MDBox
        display="flex"
        flexDirection="column"
        gap={3}
        p={{ xs: 1, sm: 2, md: 3 }}
        boxShadow={3}
        borderRadius={4}
        bgcolor="#fff"
        mb={4}
        sx={{
          width: { xs: "100%", md: "90%" },
          margin: "0 auto",
          backgroundColor: "#fff !important",
          overflowX: "auto",
        }}
      >
        <MDTypography variant="h5" fontWeight="bold" mb={2}>
          بيانات العرض
        </MDTypography>
        <MDBox
          display="flex"
          gap={2}
          alignItems="center"
          flexDirection={{ xs: "column", md: "row" }}
        >
          <MDInput
            label="عنوان العرض"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ flex: 2, width: { xs: "100%", md: "auto" } }}
            inputProps={{ dir: "rtl" }}
            fullWidth={true}
          />
          <FormControl sx={{ flex: 2, width: { xs: "100%", md: "auto" } }} fullWidth>
            <InputLabel id="target-audience-label">الجمهور المستهدف</InputLabel>
            <Select
              labelId="target-audience-label"
              value={targetAudience}
              label="الجمهور المستهدف"
              onChange={(e) => setTargetAudience(e.target.value)}
              inputProps={{ dir: "rtl" }}
              sx={{ height: 45 }}
            >
              {targetAudienceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        {errors.title && (
          <Typography color="error" fontSize={13}>
            {errors.title}
          </Typography>
        )}
        <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
          <MDInput
            label="تاريخ بداية العرض"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ dir: "rtl" }}
          />
          <MDInput
            label="تاريخ نهاية العرض"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ dir: "rtl" }}
          />
        </MDBox>
        {(errors.startDate || errors.endDate) && (
          <Typography color="error" fontSize={13}>
            {errors.startDate || errors.endDate}
          </Typography>
        )}
        <FormControl fullWidth>
          <MDBox
            display="flex"
            alignItems="center"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <FormLabel
              component="legend"
              sx={{ mb: 1, fontWeight: "bold", color: "black", minWidth: 80 }}
            >
              نوع العرض
            </FormLabel>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <RadioGroup
                aria-label="offerType"
                name="offerType"
                value={offerType}
                onChange={handleOfferTypeChange}
                sx={{ gap: 1, flexDirection: { xs: "column", md: "row" } }}
              >
                {offerTypes.map((type) => (
                  <FormControlLabel
                    key={type.id}
                    value={type.id}
                    control={<Radio />}
                    label={type.name}
                    sx={{ mr: 0, justifyContent: "flex-end" }}
                  />
                ))}
              </RadioGroup>
            )}
            <FormControl sx={{ minWidth: 140, width: { xs: "100%", md: 140 } }} fullWidth={true}>
              <InputLabel id="is-active-label">حالة العرض</InputLabel>
              <Select
                labelId="is-active-label"
                value={isActive}
                label="حالة العرض"
                onChange={(e) => setIsActive(e.target.value)}
                inputProps={{ dir: "rtl" }}
                sx={{ height: 45 }}
              >
                <MenuItem value="1">مفعل</MenuItem>
                <MenuItem value="0">غير مفعل</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSuperOffer}
                  onChange={(e) => setIsSuperOffer(e.target.checked)}
                  color="primary"
                />
              }
              label="عرض سوبر"
              sx={{ mr: 0 }}
            />
          </MDBox>
        </FormControl>
      </MDBox>
      {offerType === 1 && (
        <BundleOfferSection
          ref={bundleOfferRef}
          error={errors.bundle}
          rewardsError={errors.rewards}
          giftTypes={getAvailableGiftTypes()}
        />
      )}
      {offerType === 2 && (
        <XYOfferSection
          ref={xyOfferRef}
          error={errors.xy}
          rewardsError={errors.rewards}
          giftTypes={getAvailableGiftTypes()}
        />
      )}
      {offerType === 3 && (
        <CouponSection
          ref={couponRef}
          error={errors.coupon}
          rewardsError={errors.rewards}
          giftTypes={getAvailableGiftTypes()}
        />
      )}
      {offerType === 4 && (
        <PurchaseRewardSection
          ref={purchaseRewardRef}
          error={errors.amount}
          rewardsError={errors.rewards}
          giftTypes={getAvailableGiftTypes()}
        />
      )}
      <ConstraintsSection constraints={constraints} setConstraints={setConstraints} />
      <ExclusionsSection exclusions={exclusions} setExclusions={setExclusions} />
      <MDBox display="flex" justifyContent="center" mt={4}>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: { xs: "100%", sm: 200 }, color: "white !important" }}
          onClick={handleSave}
          fullWidth={true}
        >
          حفظ
        </Button>
      </MDBox>
    </MDBox>
  );
}
